import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { app, db, firebaseConfig } from '../firebase';

const SW_URL = `/firebase-messaging-sw.js?config=${encodeURIComponent(JSON.stringify(firebaseConfig))}`;

export function registerServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator)) return Promise.resolve(undefined);
  return navigator.serviceWorker.register(SW_URL).catch(() => undefined);
}

export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
}

export function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

export type PushResult = 'enabled' | 'denied' | 'unsupported' | 'needs-install' | 'error';

/** Ask for permission, get this device's push token and store it for the reminder function. */
export async function enablePush(uid: string): Promise<PushResult> {
  if (isIos() && !isStandalone()) return 'needs-install';
  if (!('Notification' in window) || !('serviceWorker' in navigator)) return 'unsupported';
  // Ask first, while the tap still counts as a user gesture (Safari drops the prompt after slow awaits).
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';
  const { getMessaging, getToken, isSupported } = await import('firebase/messaging');
  if (!(await isSupported().catch(() => false))) return 'unsupported';
  // Without our own key the SDK uses Firebase's shared default VAPID key, which FCM accepts.
  const vapidKey = (import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined) || undefined;
  try {
    await registerServiceWorker();
    const reg = await navigator.serviceWorker.ready;
    const token = await getToken(getMessaging(app), { vapidKey, serviceWorkerRegistration: reg });
    if (!token) return 'error';
    await setDoc(doc(db, 'users', uid, 'tokens', token), { createdAt: serverTimestamp(), ua: navigator.userAgent.slice(0, 200) });
    return 'enabled';
  } catch {
    return 'error';
  }
}

/** While the app is open, show reminders as an in-app toast instead of a system notification. */
export async function listenForeground(show: (title: string, body: string) => void): Promise<() => void> {
  if (!firebaseConfig.messagingSenderId) return () => {};
  const { getMessaging, isSupported, onMessage } = await import('firebase/messaging');
  if (!(await isSupported().catch(() => false))) return () => {};
  try {
    return onMessage(getMessaging(app), (p) => show(p.notification?.title ?? 'DuoPlate', p.notification?.body ?? ''));
  } catch {
    return () => {};
  }
}

export const PUSH_MESSAGES: Record<PushResult, string> = {
  enabled: 'Reminders are on for this device.',
  denied: 'Notifications are blocked. Allow them for DuoPlate in your browser or phone settings, then try again.',
  unsupported: 'This browser cannot receive push reminders. Try Chrome on Android or the installed app on iPhone.',
  'needs-install': 'On iPhone, add DuoPlate to your Home Screen first (Share → Add to Home Screen), open it from there, then turn reminders on.',
  error: 'Could not turn on reminders. Check your connection and try again.',
};
